export type ScenePreset = {
  name?: string;
  description?: string;
  files: string[];
  gripperFolder?: string;
  gripperXml?: string;
};

export type PresetsFileV1 = {
  schemaVersion: 1;
  presets: Record<string, ScenePreset>;
};

const isPresetMap = (value: unknown): value is Record<string, ScenePreset> => {
  if (!value || typeof value !== "object") return false;
  return Object.values(value).every(
    (entry) => entry && typeof entry === "object" && Array.isArray((entry as ScenePreset).files)
  );
};

/**
 * Migrates raw presets.json content to the current PresetsFileV1 shape.
 *
 * Handles three historical formats:
 *   - Flat map (legacy, no envelope): { preset_id: { files: [...] } }
 *   - Envelope without schemaVersion: { presets: { ... } }
 *   - v1 (current): { schemaVersion: 1, presets: { ... } }
 *
 * Returns null if the content is unrecognizable or a future unknown version.
 */
export function migratePresets(raw: unknown): PresetsFileV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  // v1: current format with schemaVersion
  if (Number(obj["schemaVersion"]) === 1) {
    if (!isPresetMap(obj["presets"])) return null;
    return { schemaVersion: 1, presets: obj["presets"] as Record<string, ScenePreset> };
  }

  // Future unknown version — do not apply.
  if ("schemaVersion" in obj && Number(obj["schemaVersion"]) > 1) return null;

  // Envelope without schemaVersion (between legacy and v1)
  if ("presets" in obj && isPresetMap(obj["presets"])) {
    return { schemaVersion: 1, presets: obj["presets"] as Record<string, ScenePreset> };
  }

  // Flat legacy format: the whole object is the preset map
  if (isPresetMap(obj)) {
    return { schemaVersion: 1, presets: obj };
  }

  return null;
}
